import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Mail, Phone, Building2, MessageSquare, User, Eye, MoreHorizontal, Trash2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { ContactSubmission } from "@shared/blog-schema";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import SuperAdminLayout from "@/components/admin/super-admin-layout";

export default function ContactManagementPage() {
  return (
    <SuperAdminLayout>
      <ContactManagementContent />
    </SuperAdminLayout>
  );
}

function ContactManagementContent() {
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [adminNotes, setAdminNotes] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: submissions = [], isLoading } = useQuery({
    queryKey: ['/api/admin/contact/submissions'],
    select: (data: ContactSubmission[]) => data
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, notes }: { id: number, status: string, notes?: string }) => 
      apiRequest("PUT", `/api/admin/contact/submissions/${id}`, { 
        body: { status, adminNotes: notes } 
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/contact/submissions'] });
      toast({
        title: "Success",
        description: "Submission updated successfully",
      });
    },
  });

  const deleteSubmissionMutation = useMutation({
    mutationFn: (id: number) => 
      apiRequest("DELETE", `/api/admin/contact/submissions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/contact/submissions'] });
      setSelectedSubmission(null);
      toast({
        title: "Success",
        description: "Submission deleted successfully",
      });
    },
  });

  const filteredSubmissions = submissions.filter(submission => {
    if (statusFilter === "all") return true;
    return submission.status === statusFilter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "new": return "bg-blue-100 text-blue-800";
      case "read": return "bg-yellow-100 text-yellow-800";
      case "replied": return "bg-green-100 text-green-800";
      case "closed": return "bg-gray-100 text-gray-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewSubmission = (submission: ContactSubmission) => {
    setSelectedSubmission(submission);
    setAdminNotes(submission.adminNotes || "");
    
    // Mark as read if it's new
    if (submission.status === "new") {
      updateStatusMutation.mutate({ id: submission.id, status: "read" });
    }
  };

  const handleUpdateStatus = (status: string) => {
    if (selectedSubmission) {
      updateStatusMutation.mutate({ 
        id: selectedSubmission.id, 
        status, 
        notes: adminNotes 
      });
      setSelectedSubmission({ ...selectedSubmission, status, adminNotes });
    }
  };

  const handleSaveNotes = () => {
    if (selectedSubmission) {
      updateStatusMutation.mutate({ 
        id: selectedSubmission.id, 
        status: selectedSubmission.status, 
        notes: adminNotes 
      });
      setSelectedSubmission({ ...selectedSubmission, adminNotes });
    }
  };

  const getSubmissionStats = () => {
    const total = submissions.length;
    const newCount = submissions.filter(s => s.status === "new").length;
    const readCount = submissions.filter(s => s.status === "read").length;
    const repliedCount = submissions.filter(s => s.status === "replied").length;
    const closedCount = submissions.filter(s => s.status === "closed").length;
    
    return { total, newCount, readCount, repliedCount, closedCount };
  };

  const stats = getSubmissionStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Contact Management</h1>
        <p className="text-gray-600 mt-1">Manage contact form submissions from the website</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-sm text-gray-600">Total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.newCount}</div>
            <p className="text-sm text-gray-600">New</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.readCount}</div>
            <p className="text-sm text-gray-600">Read</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.repliedCount}</div>
            <p className="text-sm text-gray-600">Replied</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-600">{stats.closedCount}</div>
            <p className="text-sm text-gray-600">Closed</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Submissions</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="read">Read</SelectItem>
            <SelectItem value="replied">Replied</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Submissions List */}
      {isLoading ? (
        <div className="grid gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : filteredSubmissions.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No submissions found</h3>
            <p className="text-gray-600">
              {statusFilter === "all" ? "No contact submissions yet" : `No ${statusFilter} submissions`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredSubmissions.map((submission) => (
            <Card key={submission.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">{submission.subject}</CardTitle>
                      <Badge className={getStatusColor(submission.status)}>
                        {submission.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {submission.name}
                      </div>
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {submission.email}
                      </div>
                      {submission.company && (
                        <div className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {submission.company}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(submission.createdAt)}
                      </div>
                    </div>
                    <p className="text-gray-700 mt-2 line-clamp-2">{submission.message}</p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleViewSubmission(submission)}>
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => window.open(`mailto:${submission.email}?subject=Re: ${submission.subject}`)}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Reply via Email
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => {
                          if (confirm('Are you sure you want to delete this submission?')) {
                            deleteSubmissionMutation.mutate(submission.id);
                          }
                        }}
                        className="text-red-600"
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {/* View Submission Dialog */}
      <Dialog open={!!selectedSubmission} onOpenChange={(open) => !open && setSelectedSubmission(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Contact Submission Details</DialogTitle>
            <DialogDescription>
              View and manage this contact submission
            </DialogDescription>
          </DialogHeader>
          {selectedSubmission && (
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Name</label>
                  <p className="text-gray-900">{selectedSubmission.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <p className="text-gray-900">{selectedSubmission.email}</p>
                </div>
                {selectedSubmission.company && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Company</label>
                    <p className="text-gray-900">{selectedSubmission.company}</p>
                  </div>
                )}
                {selectedSubmission.phone && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Phone</label>
                    <p className="text-gray-900">{selectedSubmission.phone}</p>
                  </div>
                )}
              </div>

              {/* Subject and Message */}
              <div>
                <label className="text-sm font-medium text-gray-700">Subject</label>
                <p className="text-gray-900 font-medium">{selectedSubmission.subject}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700">Message</label>
                <div className="bg-gray-50 p-4 rounded-lg mt-1">
                  <p className="text-gray-900 whitespace-pre-wrap">{selectedSubmission.message}</p>
                </div>
              </div>

              {/* Status and Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Status</label>
                  <Select 
                    value={selectedSubmission.status} 
                    onValueChange={handleUpdateStatus}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="read">Read</SelectItem>
                      <SelectItem value="replied">Replied</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Submitted</label>
                  <p className="text-gray-600 mt-1">{formatDate(selectedSubmission.createdAt)}</p>
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700">Admin Notes</label>
                <Textarea 
                  placeholder="Add internal notes about this submission..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
                <Button 
                  onClick={handleSaveNotes} 
                  className="mt-2" 
                  size="sm"
                  disabled={updateStatusMutation.isPending}
                >
                  Save Notes
                </Button>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  onClick={() => window.open(`mailto:${selectedSubmission.email}?subject=Re: ${selectedSubmission.subject}`)}
                  className="flex-1"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Reply via Email
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this submission?')) {
                      deleteSubmissionMutation.mutate(selectedSubmission.id);
                    }
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}